import { useState, type FormEvent } from 'react';
import { useClaves, useCrearClave, useRevocarClave } from '../api/hooks';
import { SiPuede } from '../components/SiPuede';
import { useSesion } from '../context/Sesion';
import type { AlcanceClave, ClaveApi, ClaveApiNueva } from '../types/api';
import { fecha, haceCuanto } from '../utils/formato';

const ESTADO: Record<ClaveApi['estado'], string> = { activa: 'Activa', caducada: 'Caducada', revocada: 'Revocada' };

/** Lo que hay que hacer con la clave recién creada para usarla desde Claude Code. */
/** El comando de PowerShell, con la clave ya dentro: se copia y se pega tal cual. */
export function comandoPowerShell(clave: string): string {
  return `[Environment]::SetEnvironmentVariable('DWH_API_KEY', '${clave}', 'User')`;
}

/** Lo que se le dice a Claude para comprobar que la clave funciona. */
export const FRASE_PRUEBA = 'Usa mi clave del DWH (variable DWH_API_KEY) con la API de https://dwh.fonestar.com y dime quién soy.';

function Copiar({ texto, etiqueta }: { texto: string; etiqueta: string }) {
  const [hecho, setHecho] = useState(false);
  return (
    <button
      type="button"
      className="boton boton-primario"
      onClick={() => {
        void navigator.clipboard.writeText(texto).then(() => setHecho(true));
      }}
    >
      {hecho ? 'Copiado ✓' : etiqueta}
    </button>
  );
}

/** Tras crear la clave: tres pasos para dejarla lista en Claude Code, sin tener que entender nada. */
function ClaveCreada({ c, alCerrar }: { c: ClaveApiNueva; alCerrar: () => void }) {
  return (
    <div className="aviso clave-creada" role="status">
      <h2>Tu clave está creada. Déjala lista en 3 pasos</h2>
      <p>
        Esta pantalla solo se ve una vez. Haz los pasos ahora; si cierras sin hacerlos, no pasa nada: revoca esta clave y crea otra.
      </p>
      <ol className="pasos">
        <li>
          <strong>Copia el comando</strong> (ya lleva tu clave dentro):
          <div className="paso-accion">
            <Copiar texto={comandoPowerShell(c.clave)} etiqueta="Copiar comando" />
          </div>
        </li>
        <li>
          <strong>Pégalo en PowerShell:</strong> pulsa la tecla Windows, escribe <em>PowerShell</em> y pulsa Intro. En la ventana azul, pega con{' '}
          <kbd>Ctrl</kbd>+<kbd>V</kbd> (o clic derecho) y pulsa <kbd>Intro</kbd>. No sale ningún mensaje: es normal. Cierra la ventana.
        </li>
        <li>
          <strong>Cierra Claude Code del todo y vuelve a abrirlo.</strong> Para comprobar que funciona, copia esta frase y pégasela a Claude:
          <div className="paso-accion">
            <Copiar texto={FRASE_PRUEBA} etiqueta="Copiar frase de prueba" />
          </div>
          <small className="ayuda">Claude te dirá tu nombre y tus permisos. A partir de ahí, pídele lo que necesites: «tráeme la tabla X de X3 para mi panel».</small>
        </li>
      </ol>
      <p className="ayuda">
        La clave es como una contraseña: solo vive en tu ordenador. No la pegues en ningún chat, correo ni documento. Caduca el {fecha(c.expira_at)}; entonces se crea otra igual.
      </p>
      <button type="button" className="boton boton-claro" onClick={alCerrar}>
        Hecho
      </button>
    </div>
  );
}

function NuevaClave({ alCrear }: { alCrear: (c: ClaveApiNueva) => void }) {
  const { puede } = useSesion();
  const crear = useCrearClave();
  const [nombre, setNombre] = useState('');
  const [alcance, setAlcance] = useState<AlcanceClave>('dwh.lector');
  const [dias, setDias] = useState(30);
  const [error, setError] = useState<string | null>(null);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (nombre.trim() === '') {
      setError('Ponle un nombre: para qué o dónde la vas a usar.');
      return;
    }
    setError(null);
    try {
      alCrear(await crear.mutateAsync({ nombre: nombre.trim(), alcance, dias }));
      setNombre('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido crear.');
    }
  };

  return (
    <form className="formulario" onSubmit={enviar}>
      <fieldset>
        <legend>Nueva clave</legend>
        <div className="campo">
          <label htmlFor="clave-nombre">Nombre</label>
          <input id="clave-nombre" value={nombre} maxLength={100} placeholder="Claude Code en mi portátil" onChange={(e) => setNombre(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="clave-alcance">Permisos</label>
          <select id="clave-alcance" value={alcance} onChange={(e) => setAlcance(e.target.value as AlcanceClave)}>
            <option value="dwh.lector">Solo lectura</option>
            {puede('dwh.editor') && <option value="dwh.editor">Lectura y edición de la configuración</option>}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="clave-dias">Caduca en</label>
          <select id="clave-dias" value={dias} onChange={(e) => setDias(Number(e.target.value))}>
            <option value={7}>7 días</option>
            <option value={30}>30 días</option>
            <option value={90}>90 días</option>
          </select>
        </div>
        {error && <p className="error-campo" role="alert">{error}</p>}
        <button type="submit" className="boton boton-primario" disabled={crear.isPending}>
          Crear clave
        </button>
      </fieldset>
    </form>
  );
}

function Lista({ todas }: { todas: boolean }) {
  const claves = useClaves(todas);
  const revocar = useRevocarClave();
  if (claves.isLoading) return <p className="cargando">Cargando…</p>;
  if (claves.error) return <div className="aviso aviso-error">{claves.error.message}</div>;
  if ((claves.data ?? []).length === 0) return <p>No hay claves.</p>;
  return (
    <table className="tabla">
      <thead>
        <tr>
          <th>Nombre</th>
          {todas && <th>De</th>}
          <th>Clave</th>
          <th>Permisos</th>
          <th>Estado</th>
          <th>Caduca</th>
          <th>Último uso</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {(claves.data ?? []).map((c) => (
          <tr key={c.id} className={c.estado === 'activa' ? undefined : 'inactiva'}>
            <td>{c.nombre}</td>
            {todas && <td>{c.usuario.nombre ?? c.usuario.email}</td>}
            <td>
              <code>{c.prefijo}…</code>
            </td>
            <td>{c.alcance === 'dwh.editor' ? 'lectura y edición' : 'solo lectura'}</td>
            <td title={c.revocada_por ? `por ${c.revocada_por}` : undefined}>{ESTADO[c.estado]}</td>
            <td>{fecha(c.expira_at)}</td>
            <td title={c.ultimo_uso_ip ?? undefined}>{c.ultimo_uso_at ? haceCuanto(c.ultimo_uso_at) : 'nunca'}</td>
            <td>
              {c.estado === 'activa' && (
                <button
                  type="button"
                  className="boton boton-peligro"
                  disabled={revocar.isPending}
                  onClick={() => {
                    if (window.confirm(`¿Revocar «${c.nombre}»? Deja de funcionar al momento.`)) revocar.mutate(c.id);
                  }}
                >
                  Revocar
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ClavesPage() {
  const [creada, setCreada] = useState<ClaveApiNueva | null>(null);
  const [todas, setTodas] = useState(false);
  return (
    <>
      <h1>Claves de API</h1>
      <p>
        Claves personales para usar la API desde Claude Code o un script. Actúan en tu nombre (queda en la auditoría), nunca con más
        permisos que los tuyos ni como administrador, y caducan solas. Se gestionan solo desde aquí.
      </p>
      {creada ? <ClaveCreada c={creada} alCerrar={() => setCreada(null)} /> : <NuevaClave alCrear={setCreada} />}
      <h2>{todas ? 'Claves de todos' : 'Mis claves'}</h2>
      <SiPuede rol="dwh.admin">
        <label className="filtros">
          <input type="checkbox" checked={todas} onChange={(e) => setTodas(e.target.checked)} /> Ver las de todos
        </label>
      </SiPuede>
      <Lista todas={todas} />
    </>
  );
}
